import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeNotesSearchVariant = 'wedge-notes-search';
export const isWedgeNotesSearchVariant = (variant: unknown): variant is WedgeNotesSearchVariant => variant === 'wedge-notes-search';

export type WedgeNotesListLook = 'notes-chip' | 'notes-drafts' | 'notes-clear' | 'notes-done' | 'notes-undo';

const NOTES_LIST_LOOKS = new Set<string>(['notes-chip', 'notes-drafts', 'notes-clear', 'notes-done', 'notes-undo']);
export const isWedgeNotesListLook = (look: string): look is WedgeNotesListLook => NOTES_LIST_LOOKS.has(look);

/* Released Notes app list pane controls (the filter chips, the Drafts toggle and its Clear button, the keyboard dock Done button and the toast Undo
   button). The shipped `NOTES_CSS` sheet styles each one by class and the caller keeps the `rvn-*` class. A look leaves out every property that a
   state rule sets (`.rvn-chip:hover` and `.on` recolor the whole border, the fill and the ink; `.gbtn:hover` sets the fill and the ink), the radius,
   the outline and the box shadow (`.wedge-scope button:focus-visible` draws the 8px ring above the class), the font family (`.wedge-scope button`
   sets it above the `font` shorthand of the class) and the transition. The Drafts toggle sets the two values that the source writes inline
   (`padding: 0` and `gap: 6px`) and leaves the rest to `.rvn-group.toggle`. The dock Done button leaves out the border: the class draws the left
   hairline only, and the shorthand would clear it.
   No look draws a transparent target. The chips sit in a strip that scrolls on the x axis, which clips on the y axis too, so a region taller than the
   30px box would add a scroll range. The Drafts toggle, Clear and Undo are inline runs of text beside a neighbor (the label, the other button), so a
   region would overlap it. Done is 44px tall already. */
const LOOKS: Record<WedgeNotesListLook, () => CSSProperties> = {
  'notes-chip': () => ({
    flex: 'none', height: 30, padding: '0 12px', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
  }),
  'notes-drafts': () => ({padding: 0, gap: 6}),
  'notes-clear': () => ({marginLeft: 'auto', fontSize: 12, fontWeight: 500, padding: '2px 8px', cursor: 'pointer'}),
  'notes-done': () => ({fontSize: 16, fontWeight: 600, padding: '0 12px 0 10px', height: 44, flex: 'none', cursor: 'pointer'}),
  'notes-undo': () => ({fontWeight: 600, padding: '4px 8px', cursor: 'pointer'}),
};

export const wedgeNotesListStyle = (look: WedgeNotesListLook): CSSProperties => LOOKS[look]();

/* Every look of this module draws no transparent target. */
export const isWedgeNotesListNoHitLook = (look: string): boolean => isWedgeNotesListLook(look);

/* Released search input of the Notes list (`.rvn-search input`): a bare native input that fills the pill, with no fill of its own and the ink of the
   page at 16px. The class keeps the border, the outline and the placeholder. The caller owns the value, the ref and the handlers. */
export function wedgeNotesSearchStyle(): CSSProperties {
  return {flex: 1, minWidth: 0, background: 'transparent', color: wedgeExactRole('label'), fontSize: 16};
}
